import React, { useState, useEffect } from "react";
import Select from "react-select";
import { message, Spin } from "antd";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import {
  useGetCompanySettingsQuery,
  useSaveCompanySettingsMutation,
  useGetCountriesQuery,
  useLazyGetStatesQuery,
} from "../../../../services/settings.service";
import { companySettingsSchema } from "../../../../utils/validationSchemas";
import { useFormValidation } from "../../../../hooks/useFormValidation";

const Settings = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    address: "",
    countryId: null,
    city: "",
    stateId: null,
    postalCode: "",
    email: "",
    phone: "",
    mobile: "",
    fax: "",
    website: "",
  });

  // Validation Hook
  const { errors, validateForm, validateField, touchField, hasFieldError, getFieldError } = useFormValidation(companySettingsSchema);

  // API Hooks
  const { data: settingsData, isLoading } = useGetCompanySettingsQuery();
  const { data: countriesData } = useGetCountriesQuery();
  const [getStates, { data: statesData }] = useLazyGetStatesQuery();
  const [saveSettings, { isLoading: isSaving }] = useSaveCompanySettingsMutation();

  const countries = countriesData?.data || [];
  const states = statesData?.data || [];

  // Load initial data
  useEffect(() => {
    if (settingsData?.data) {
      const data = settingsData.data;
      setFormData({
        companyName: data.companyName || "",
        contactPerson: data.contactPerson || "",
        address: data.address || "",
        countryId: data.countryId || null,
        city: data.city || "",
        stateId: data.stateId || null,
        postalCode: data.postalCode || "",
        email: data.email || "",
        phone: data.phone || "",
        mobile: data.mobile || "",
        fax: data.fax || "",
        website: data.website || "",
      });

      // Load states if country is selected
      if (data.countryId) {
        getStates(data.countryId);
      }
    }
  }, [settingsData, getStates]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: value
    };
    setFormData(newFormData);

    // Validate field on blur/change
    validateField(name, value, newFormData);
  };

  // Handle blur event
  const handleBlur = (fieldName) => {
    touchField(fieldName);
    validateField(fieldName, formData[fieldName], formData);
  };

  // Handle country change
  const handleCountryChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      countryId: selectedOption?.value || null,
      stateId: null // Reset state when country changes
    }));
    if (selectedOption?.value) {
      getStates(selectedOption.value);
    }
  };

  // Handle state change
  const handleStateChange = (selectedOption) => {
    setFormData(prev => ({
      ...prev,
      stateId: selectedOption?.value || null
    }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate entire form
    const { isValid } = await validateForm(formData);

    if (!isValid) {
      // Validation errors will be displayed inline
      return;
    }

    try {
      // If validation passes, save settings
      await saveSettings(formData).unwrap();
      message.success('Company settings saved successfully');
    } catch (error) {
      // Handle API errors
      message.error(error?.data?.message || 'Failed to save company settings');
    }
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#008a42" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#008a42",
      },
    }),
  };

  // Convert countries to select options
  const countryOptions = countries.map(country => ({
    label: country.name,
    value: country.id
  }));

  // Convert states to select options
  const stateOptions = states.map(state => ({
    label: state.name,
    value: state.id
  }));

  // Get selected country
  const selectedCountry = countryOptions.find(opt => opt.value === formData.countryId);

  // Get selected state
  const selectedState = stateOptions.find(opt => opt.value === formData.stateId);

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        {/* Page Content */}
        <div className="content container-fluid">
          <div className="row">
            <div className="col-md-8 offset-md-2 box-align">
              {/* Page Header */}
              <Breadcrumbs maintitle="Company Settings" />

              {/* /Page Header */}
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">
                        Company Name <span className="text-danger">*</span>
                      </label>
                      <input
                        className={`form-control ${hasFieldError('companyName') ? 'is-invalid' : ''}`}
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('companyName')}
                        required
                      />
                      {getFieldError('companyName') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('companyName')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Contact Person</label>
                      <input
                        className={`form-control ${hasFieldError('contactPerson') ? 'is-invalid' : ''}`}
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('contactPerson')}
                        type="text"
                      />
                      {getFieldError('contactPerson') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('contactPerson')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Address</label>
                      <input
                        className={`form-control ${hasFieldError('address') ? 'is-invalid' : ''}`}
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('address')}
                        type="text"
                      />
                      {getFieldError('address') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('address')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-6 col-lg-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Country</label>
                      <Select
                        options={countryOptions}
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        placeholder="Select Country"
                        styles={customStyles}
                        isClearable
                      />
                      {getFieldError('countryId') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('countryId')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-6 col-lg-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label">City</label>
                      <input
                        className={`form-control ${hasFieldError('city') ? 'is-invalid' : ''}`}
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('city')}
                        type="text"
                      />
                      {getFieldError('city') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('city')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-6 col-lg-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label">State/Province</label>
                      <Select
                        options={stateOptions}
                        value={selectedState}
                        onChange={handleStateChange}
                        placeholder="Select State"
                        styles={customStyles}
                        isClearable
                        isDisabled={!formData.countryId}
                      />
                      {getFieldError('stateId') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('stateId')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6 col-md-6 col-lg-3">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Postal Code</label>
                      <input
                        className={`form-control ${hasFieldError('postalCode') ? 'is-invalid' : ''}`}
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('postalCode')}
                        type="text"
                      />
                      {getFieldError('postalCode') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('postalCode')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Email</label>
                      <input
                        className={`form-control ${hasFieldError('email') ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('email')}
                        type="email"
                      />
                      {getFieldError('email') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('email')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Phone Number</label>
                      <input
                        className={`form-control ${hasFieldError('phone') ? 'is-invalid' : ''}`}
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('phone')}
                        type="text"
                      />
                      {getFieldError('phone') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('phone')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Mobile Number</label>
                      <input
                        className={`form-control ${hasFieldError('mobile') ? 'is-invalid' : ''}`}
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('mobile')}
                        type="text"
                      />
                      {getFieldError('mobile') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('mobile')}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Fax</label>
                      <input
                        className={`form-control ${hasFieldError('fax') ? 'is-invalid' : ''}`}
                        name="fax"
                        value={formData.fax}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('fax')}
                        type="text"
                      />
                      {getFieldError('fax') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('fax')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="input-block mb-3">
                      <label className="col-form-label">Website Url</label>
                      <input
                        className={`form-control ${hasFieldError('website') ? 'is-invalid' : ''}`}
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        onBlur={() => handleBlur('website')}
                        type="text"
                      />
                      {getFieldError('website') && (
                        <div className="text-danger small mt-1">
                          {getFieldError('website')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="submit-section">
                  <button
                    className="btn btn-primary submit-btn"
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        {/* /Page Content */}
      </div>
    </>
  );
};

export default Settings;
